import React from 'react';
import { Button } from '../../../../../components/ui/button';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { MultiSelect, Option } from '../../../../../components/ui/multi-select';

interface Document {
  id: string;
  fileName: string;
  uploadDate: string;
}

interface Certification extends Option {
  label: string;
  value: string;
}

interface AddTeamMemberModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTeamMember: any;
  setSelectedTeamMember: (member: any) => void;
  handleTeamMemberDocumentUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSaveTeamMember: () => void;
  roles: string[];
  certifications: Certification[];
  isAddingRole: boolean;
  setIsAddingRole: (value: boolean) => void;
  isAddingCertification: boolean;
  setIsAddingCertification: (value: boolean) => void;
  newRole: string;
  setNewRole: (value: string) => void;
  newCertification: string;
  setNewCertification: (value: string) => void;
  handleAddRole: () => void;
  handleAddCertification: () => void;
}

export const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
  isOpen,
  onOpenChange,
  selectedTeamMember,
  setSelectedTeamMember,
  handleTeamMemberDocumentUpload,
  handleSaveTeamMember,
  roles,
  certifications,
  isAddingRole,
  setIsAddingRole,
  isAddingCertification,
  setIsAddingCertification,
  newRole,
  setNewRole,
  newCertification,
  setNewCertification,
  handleAddRole,
  handleAddCertification,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button 
          size="sm"
          className="bg-[#e04530] text-white hover:bg-[#e04530]/90 flex items-center gap-2 min-w-[140px] justify-center"
        >
          <Plus className="h-4 w-4" />
          Add Member
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {selectedTeamMember?.id ? 'Edit Team Member' : 'Add New Team Member'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="Enter name"
                value={selectedTeamMember?.name || ''}
                onChange={(e) => {
                  if (selectedTeamMember) {
                    setSelectedTeamMember({
                      ...selectedTeamMember,
                      name: e.target.value,
                    });
                  }
                }}
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select
                value={selectedTeamMember?.role || ''}
                onValueChange={(value) => {
                  if (selectedTeamMember) {
                    setSelectedTeamMember({
                      ...selectedTeamMember,
                      role: value,
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                  <Dialog open={isAddingRole} onOpenChange={setIsAddingRole}>
                    <DialogTrigger asChild>
                      <Button
                        className="flex items-center w-full px-2 py-1.5 text-sm hover:bg-gray-100 text-[#e04530]"
                        variant="ghost"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add New Role
                         </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Role</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Role Name</Label>
                          <Input
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value)}
                            placeholder="Enter role name"
                          />
                        </div>      
                        <div className="flex justify-end">
                          <Button
                            onClick={handleAddRole}
                            className="bg-[#e04530] text-white hover:bg-[#e04530]/90"
                            disabled={!newRole.trim()}
                          >
                            Add Role
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email"
                value={selectedTeamMember?.email || ''}
                onChange={(e) => {
                  if (selectedTeamMember) {
                    setSelectedTeamMember({
                      ...selectedTeamMember,
                      email: e.target.value,
                    });
                  }
                }}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                placeholder="Enter phone number"
                value={selectedTeamMember?.phone || ''}
                onChange={(e) => {
                  if (selectedTeamMember) {
                    setSelectedTeamMember({
                      ...selectedTeamMember,
                      phone: e.target.value,
                    });
                  }
                }}
              />
            </div>
          </div>
          <div>
            <Label>Certifications</Label>
            <MultiSelect
              options={certifications}
              selected={selectedTeamMember?.certifications?.map((cert: Certification) => cert.value) || []}
              onChange={(values) => {
                if (selectedTeamMember) {
                  const selectedCerts = certifications.filter((cert: Certification) => 
                    values.includes(cert.value)
                  );
                  setSelectedTeamMember({
                    ...selectedTeamMember,
                    certifications: selectedCerts,
                  });
                } else {
                  // Handle case when adding new team member
                  const selectedCerts = certifications.filter((cert: Certification) => 
                    values.includes(cert.value)
                  );
                  setSelectedTeamMember({
                    name: '',
                    email: '',
                    phone: '',
                    role: '',
                    certifications: selectedCerts,
                    documents: [],
                  });
                }
              }}
              placeholder="Select certifications"
            />
            <Dialog open={isAddingCertification} onOpenChange={setIsAddingCertification}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="mt-2 w-full flex items-center justify-center gap-2 text-[#e04530] hover:text-[#e04530]/90"
                >
                  <Plus className="h-4 w-4" />
                  Add New Certification
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Certification</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Certification Name</Label>
                    <Input
                      value={newCertification}
                      onChange={(e) => setNewCertification(e.target.value)}
                      placeholder="Enter certification name"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      onClick={handleAddCertification}
                      className="bg-[#e04530] text-white hover:bg-[#e04530]/90"
                      disabled={!newCertification.trim()}
                    >
                      Add Certification
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div>
            <Label>Documents</Label>
            <Input
              type="file"
              onChange={handleTeamMemberDocumentUpload}
              multiple
              accept=".pdf,.doc,.docx"
            />
            {selectedTeamMember?.documents && selectedTeamMember.documents.length > 0 && (
              <div className="mt-2">
                <Label>Uploaded Documents</Label>
                <div className="space-y-2">
                  {selectedTeamMember.documents.map((doc: Document) => (
                    <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm">{doc.fileName}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(doc.uploadDate).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setSelectedTeamMember(null);
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveTeamMember}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 